import { ReactNode } from 'react';

import {
   EmergencyNotificationConfiguration,
   EmergencyNotificationConfigurationQuery,
   EmergencyNotificationConfigurationSetCommand,
   EmergencyNotificationConfigurationSimpleQuery,
} from '@dreryk/egabinet-emergency-notification-frontend-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import { ActionEventInterceptor, dispatchSnackbarPositiveEvent } from '@dreryk/react-components';
import { refetchCqrsQuery, useCqrsCommand } from '@dreryk/command-query-react-common';

import {
   EmergencyNotificationConfigurationFormBean,
   EmergencyNotificationConfigurationFormController,
} from '../EmergencyNotificationConfigurationForm.types';
import { translationKeys } from '../../../translations';

type EmergencyNotificationConfigurationSetActionEventInterceptorProps = Readonly<
   Pick<EmergencyNotificationConfigurationFormController, 'handleSubmit'> & {
      children: ReactNode;
   }
> & { emergencyNotificationConfiguration: EmergencyNotificationConfiguration };

export const EmergencyNotificationConfigurationSetActionEventInterceptor = (
   props: EmergencyNotificationConfigurationSetActionEventInterceptorProps
): JSX.Element => {
   const { executeAsync: setSmsConfiguration } = useCqrsCommand<void, EmergencyNotificationConfigurationFormBean>(
      (parameters) => {
         const command = new EmergencyNotificationConfigurationSetCommand();

         command.smsConfiguration = parameters.smsConfigurationEnabled
            ? {
                 enabled: true,
                 messageCustomizablePart: parameters.messageCustomizablePart!,
                 contactPersons: parameters.contactPersons.map((contactPerson) => ({
                    name: contactPerson.name!,
                    phoneNumber: contactPerson.phoneNumber!,
                 })),
              }
            : {
                 enabled: false,
                 messageCustomizablePart:
                    props.emergencyNotificationConfiguration.smsConfiguration.messageCustomizablePart,
                 contactPersons: props.emergencyNotificationConfiguration.smsConfiguration.contactPersons.map(
                    (contactPerson) => ({
                       name: contactPerson.name,
                       phoneNumber: contactPerson.phoneNumber,
                    })
                 ),
              };

         command.inAppConfiguration = parameters.inAppConfigurationEnabled
            ? {
                 enabled: true,
                 responseTeamPractitionerIds: parameters.responseTeamPractitioners.value.map(({ practitioner }) =>
                    PractitionerId.of(practitioner.id)
                 ),
              }
            : {
                 enabled: false,
                 responseTeamPractitionerIds:
                    props.emergencyNotificationConfiguration.inAppConfiguration.responseTeamPractitionerIds,
              };

         return command;
      },
      {
         onSuccess: () => {
            dispatchSnackbarPositiveEvent(translationKeys.successfullySetConfiguration);
            refetchCqrsQuery(EmergencyNotificationConfigurationQuery);
            refetchCqrsQuery(EmergencyNotificationConfigurationSimpleQuery);
         },
      }
   );

   return (
      <ActionEventInterceptor action={props.handleSubmit(setSmsConfiguration)}>{props.children}</ActionEventInterceptor>
   );
};
